<template>
  <div id="container-main">
    <div class="dashboard-row">
      <!-- Painel de Status -->
      <div class="card-dashboard status-card">
        <h4>Status</h4>
        <div class="status-content">
          <ul class="legend">
            <li><span class="dot available"></span> Ramais Disponíveis</li>
            <li><span class="dot ringing"></span> Tocando</li>
            <li><span class="dot busy"></span> Em atendimento</li>
            <li><span class="dot pause"></span> Em pausa</li>
          </ul>
          <apexchart type="pie" height="260" width="260" :options="statusChartOptions" :series="statusChartData" />
        </div>
      </div>
      <!-- Painel de chamadas em fila -->
      <div class="card-dashboard wait-card">
        <h4>Tempo de espera em fila ({{ filteredCallsInQueue.length }})</h4>
        <div v-if="filteredCallsInQueue.length === 0" class="wait-info">
          <i class="fas fa-phone-alt wait-icon"></i>
          <p>Nenhuma chamada em espera.</p>
        </div>
        <div v-else>
          <table class="wait-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Fila</th>
                <th>Tempo de Espera</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(call, idx) in paginatedCallsInQueue" :key="call.number + '-' + idx">
                <td>{{ call.number }}</td>
                <td>{{ call.queue }}</td>
                <td :class="{ 'highlight-wait': call.waitingTime > 30 }">
                  {{ formatWaitingTime(call.waitingTime) }}
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="totalPages > 1" class="pagination">
            <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1">Anterior</button>
            <span>Página {{ currentPage }} de {{ totalPages }}</span>
            <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">Próxima</button>
          </div>
        </div>
      </div>

      <!-- Painel de Agente atualizado só aqui -->
      <div class="card-dashboard agent-card">
        <h4>Tempo Médio de Atendimento (TMA)</h4>
        <small class="text-muted" style="margin-right: 10px; color: orange !important">
          <i class="fas fa-info-circle"></i>
          Dados atualizados automaticamente a cada 30 segundos.
        </small>
        <apexchart ref="tmaChart" type="bar" height="200" :options="tmaChartOptions" :series="tmaSeries" />
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import axios from "axios";
import moment from "moment";
import momentDuration from "moment-duration-format";
momentDuration(moment);

export default {
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    callsInQueueDetails: {
      type: Array,
      default: () => [],
    },
    selectedQueues: {
      type: Array,
      default: () => [],
    },
    queueIdToName: {
      type: Object,
      default: () => ({}),
    },
    callsInAttendance: {
      type: Number,
      default: 0,
    },
    callsRinging: {
      type: Number,
      default: 0,
    },
    pausedCount: {
      type: Number,
      default: 0,
    },
    filteredUsers: {
      type: Array,
      default: () => [],
    },
    onlineCount: {
      type: Number,
      default: 0,
    },
    offlineCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      statusChartOptions: {
        chart: {
          type: "pie",
        },
        labels: [
          "Disponíveis",
          "Tocando",
          "Em atendimento",
          "Em pausa",
          "Ramais Offline",
        ],
        colors: ["#2ecc71", "#3498db", "#f1c40f", "#e67e22", "#e74c3c"],
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(0) + "%";
          },
        },
      },

      currentPage: 1,
      pageSize: 5,

      // Dados do gráfico TMA
      tmaSeries: [{ data: [] }],
      tmaChartOptions: {
        chart: {
          type: "bar",
          height: 300,
        },
        plotOptions: {
          bar: {
            barHeight: "80%",
            distributed: true,
            horizontal: true,
            dataLabels: {
              position: "bottom",
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          min: 0,
          max: 900, // Exemplo: até 15 minutos
          tickAmount: 6, // define quantos marcadores mostrar (0, 3min, 6min, 9min, 12min, 15min)
          categories: [],
          labels: {
            formatter: (value) => this.formatDuration(value),
          },
        },
        tooltip: {
          y: {
            formatter: (value) => this.formatDuration(value),
            title: {
              formatter: () => "TMA: ",
            },
          },
        },
        noData: {
          text: "Sem dados para exibir no momento...",
        },
        colors: ["#00E396", "#FF4560", "#008FFB", "#FEB019", "#775DD0"],
      },
    };
  },
  computed: {
    totalRamais() {
      return this.onlineCount + this.offlineCount;
    },
    statusChartData() {
      const available = this.onlineCount || 0;
      const ringing = this.callsRinging || 0;
      const inAttendance = this.callsInAttendance || 0;
      const paused = this.pausedCount || 0;
      return [available, ringing, inAttendance, paused];
    },

    filteredCallsInQueue() {
      if (!this.selectedQueues.length || this.selectedQueues.includes("*")) {
        return this.callsInQueueDetails;
      }
      const selectedQueueNames = this.selectedQueues.map(
        (id) => this.queueIdToName[id] || id
      );
      return this.callsInQueueDetails.filter((call) =>
        selectedQueueNames.includes(call.queue)
      );
    },

    totalPages() {
      return Math.ceil(this.filteredCallsInQueue.length / this.pageSize) || 1;
    },
    paginatedCallsInQueue() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredCallsInQueue.slice(start, start + this.pageSize);
    },
  },
  watch: {
    callsInQueueDetails: {
      handler(newVal, oldVal) {
        if (newVal.length !== oldVal.length) {
          this.currentPage = 1;
        }
      },
      deep: true,
    },
    selectedQueues() {
      this.fetchTmaData();
    },
  },
  methods: {
    formatWaitingTime(seconds) {
      if (!seconds || seconds <= 0) return "00:00";
      const min = Math.floor(seconds / 60)
        .toString()
        .padStart(2, "0");
      const sec = (seconds % 60).toString().padStart(2, "0");
      return `${min}:${sec}`;
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    formatDuration(seconds) {
      if (!seconds) return "00:00:00";
      return seconds >= 86400
        ? moment.duration(seconds, "seconds").format("hh:mm:ss").replace(",", ".")
        : moment.utc(seconds * 1000).format("HH:mm:ss");
    },
    fetchTmaData() {
      axios
        .get(`${window.location.href}/averageServiceTime?queue=${this.selectedQueues.join(",")}`)
        .then(({ data }) => {
          if (this.selectedQueues[0] == "*") {
            const top = data
              .slice()
              .sort((a, b) => b.average - a.average)
              .slice(0, 5);

            this.tmaChartOptions.xaxis.categories = top.map(el => el.alias);
            this.tmaSeries[0].data = top.map(el => el.average);
            this.$refs.tmaChart.refresh();
          } else {
            const selectedQueueNames = this.selectedQueues
              .map(id => this.queueIdToName[id])
              .filter(Boolean);

            const filteredData = data.filter(d => selectedQueueNames.includes(d.queuename));

            this.tmaChartOptions.xaxis.categories = filteredData.map(el => el.alias);
            this.tmaSeries[0].data = filteredData.map(el => el.average);
            this.$refs.tmaChart.refresh();
          }
        })
        .catch(() => {
          this.tmaChartOptions.xaxis.categories = [];
          this.tmaSeries[0].data = [];
        });
    },
  },
  mounted() {
    this.fetchTmaData();
    setInterval(() => {
      this.fetchTmaData();
    }, 30000);
  },
};
</script>

<style scoped>
.wait-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 10px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.06);
  background-color: #fff;
}

.wait-table th,
.wait-table td {
  padding: 10px 12px;
  text-align: center;
  font-size: 13px;
}

.wait-table th {
  background-color: #f4f7fa;
  color: #333;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 12px;
  border-bottom: 1px solid #e0e6ed;
}

.wait-table td {
  color: #555;
  border-bottom: 1px solid #f1f4f8;
  transition: background-color 0.2s ease;
}

.wait-table tr:hover td {
  background-color: #f9fbfd;
}

.wait-table tr:last-child td {
  border-bottom: none;
}

/* Destaque para chamadas com tempo acima de 30s */
.highlight-wait {
  background-color: #fff6e3 !important;
  color: #d35400;
  font-weight: 600;
}

#container-main {
  padding: 20px;
  background: #f5f7fa;
}

.dashboard-row {
  display: flex;
  justify-content: space-between;
  gap: 20px;
}

.card-dashboard {
  background: white;
  border-radius: 12px;
  padding: 20px;
  flex: 1;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1);
  min-width: 300px;
  max-width: 32%;
}

.card-dashboard h4 {
  margin-bottom: 15px;
  font-size: 18px;
  color: #333;
}

.status-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.status-content apexchart {
  flex-shrink: 0;
  width: 260px !important;
  height: 260px !important;
}

.legend {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 14px;
  color: #444;
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-weight: 600;
}

.legend li {
  display: flex;
  align-items: center;
  gap: 10px;
}

.legend .dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
}

.available {
  background: #27ae60;
  box-shadow: 0 0 8px #27ae60aa;
}

.ringing {
  background: #2980b9;
  box-shadow: 0 0 8px #2980b9aa;
}

.busy {
  background: #f39c12;
  box-shadow: 0 0 8px #f39c12aa;
}

.pause {
  background: #d35400;
  box-shadow: 0 0 8px #d35400aa;
}

.unreachable {
  background: #c0392b;
  box-shadow: 0 0 8px #c0392baa;
}

.agent-info p {
  margin: 5px 0;
  font-size: 14px;
}

.wait-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 180px;
  color: #888;
  font-size: 14px;
}

.wait-icon {
  font-size: 48px;
  color: #ccc;
  margin-bottom: 10px;
}

.pagination {
  margin-top: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
}

.pagination button {
  padding: 6px 12px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 5px !important;
  font-size: 14px;
  cursor: pointer;
}

.pagination button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
